import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/student-book-report/StudentBookReportAction";
import {
  ExportButton,
  copy,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  print,
  redirectToPages
} from "../../../../../../_helpers/Helpers";
import {
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { useMonthlyReportUIContext } from "./MonthlyReportUIContext";
import { MonthlyReportFilter } from "./monthly-report-filter/MonthlyReportFilter";
import { MonthlyReportTable } from "./monthly-report-table/MonthlyReportTable";

export function MonthlyReportCard() {
  // Monthly Report UI Context
  const monthlyReportUIContext = useMonthlyReportUIContext();
  const monthlyReportUIProps = useMemo(() => {
    return {
      queryParams: monthlyReportUIContext.queryParams
    };
  }, [monthlyReportUIContext]);
    
  // Getting curret state of Monthly Report from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentBookReport }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized } = getAuthorities(authorities);

  // Monthly Report Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğrenci Aylık Kitap Okuma Raporunu Excele Aktarır */
  const exportMonthlyReportToExcel = () => {
    dispatch(actions.getAllMonthlyReport(monthlyReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [25, 25, 30, 25, 20, 20];
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Sayısı", "Sayfa Sayısı", "Tarih"];
      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookCount,
        entity.pageNumberCount,
        entity.date
      ]));
      const filename = monthlyReportUIProps.queryParams.term + " - " + (monthlyReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Aylık Kitap Okuma Raporu";
      const sheetname = "Aylık Kitap Okuma Raporu"
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğrenci Aylık Kitap Okuma Raporunu PDF e Aktarır */
  const exportMonthlyReportToPDF = () => {
    dispatch(actions.getAllMonthlyReport(monthlyReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Şube",
          rows: entities.map(entity => entity.section)
        },
        {
          header: "Öğrenci Numarası",
          rows: entities.map(entity => entity.studentNumber)
        },
        {
          header: "Adı Soyadı",
          rows: entities.map(entity => entity.studentName)
        },
        {
          header: "Kitap Sayısı",
          rows: entities.map(entity => entity.bookCount)
        },
        {
          header: "Sayfa Sayısı",
          rows: entities.map(entity => entity.pageNumberCount)
        },
        {
          header: "Tarih",
          rows: entities.map(entity => entity.date)
        }
      ];
  
      const filename = monthlyReportUIProps.queryParams.term + " - " + (monthlyReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Aylık Kitap Okuma Raporu";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Öğrenci Aylık Kitap Okuma Yazdırma Penceresini Açar */
  const printMonthlyReport = () => {
    dispatch(actions.getAllMonthlyReport(monthlyReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = monthlyReportUIProps.queryParams.term + " - " + (monthlyReportUIContext.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Aylık Kitap Okuma Raporu";
      const properties = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Sayısı", "Sayfa Sayısı", "Tarih"];

      entities.forEach(entity => {
        data.push({
          "Şube": entity.section,
          "Öğrenci Numarası": entity.studentNumber,
          "Adı Soyadı": entity.studentName,
          "Kitap Sayısı": entity.bookCount,
          "Sayfa Sayısı": entity.pageNumberCount,
          "Tarih": entity.date
        });
      });

      print({ data, properties, header });
    });
  }

  /** Belli Bir Öğrenci Aylık Kitap Okuma Raporunu Panoya Kopyalar */
  const copyMonthlyReport = () => {
    dispatch(actions.getAllMonthlyReport(monthlyReportUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = monthlyReportUIProps.queryParams.term + " - " + (monthlyReportUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Öğrenci Aylık Kitap Okuma Raporu";
      const columns = ["Şube", "Öğrenci Numarası", "Adı Soyadı", "Kitap Sayısı", "Sayfa Sayısı", "Tarih"];
      entities.forEach(entity => rows.push([
        entity.section,
        entity.studentNumber,
        entity.studentName,
        entity.bookCount,
        entity.pageNumberCount,
        entity.date
      ]));
      copy({ header, columns, rows });
    });
  }

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title="Aylık Kitap Okuma Raporu">
        {
          isExportationAuthorized &&
          <CardHeaderToolbar>
            <ExportButton
              exportToExcel={exportMonthlyReportToExcel}
              exportToPDF={exportMonthlyReportToPDF}
              print={printMonthlyReport}
              copy={copyMonthlyReport}
            />
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <MonthlyReportFilter />
        <MonthlyReportTable />
      </CardBody>
    </Card>
  );
}